import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getUserInfoApi,
  deleteAccountApi,
} from "../../services/authApi";
import "./DeleteAccountPage.css";

const DeleteAccountPage = () => {
  const navigate = useNavigate();
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [step, setStep] = useState("initial");
  const [confirmText, setConfirmText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [userData, setUserData] = useState(null);
  const [alreadyRequested, setAlreadyRequested] = useState(false);

  useEffect(() => {
    loadUserInfo();
  }, []);

  const loadUserInfo = async () => {
    setLoading(true);
    setError("");

    try {
      const response = await getUserInfoApi();

      let userData = null;

      if (response?.data?.success && response?.data?.data?.data?.[0]) {
        userData = response.data.data.data[0];
      } else if (response?.success && response?.data?.data?.[0]) {
        userData = response.data.data[0];
      } else {
        const localUserData = localStorage.getItem("userData");
        if (localUserData) {
          try {
            userData = JSON.parse(localUserData);
          } catch (e) {
            console.error("خطا در پارس اطلاعات کاربر:", e);
          }
        }
      }

      if (userData) {
        setUserData(userData);
        setUserInfo(userData);
      } else {
        setError("خطا در دریافت اطلاعات کاربری");
      }
    } catch (err) {
      console.error("خطا در دریافت اطلاعات کاربر:", err);

      const localUserData = localStorage.getItem("userData");
      if (localUserData) {
        try {
          const parsedData = JSON.parse(localUserData);
          setUserData(parsedData);
          setUserInfo(parsedData);
        } catch (e) {
          console.error(e);
        }
      } else {
        setError("خطا در دریافت اطلاعات کاربری");
      }
    } finally {
      setLoading(false);
    }
  };

  const getUserTypeDisplay = () => {
    if (!userInfo?.type_user) return "کاربر عادی";
    const types = userInfo.type_user;
    if (types.includes("userPremium")) return "کاربر ویژه";
    if (types.includes("subtitlesManager")) return "مدیر زیرنویس";
    if (types.includes("appAndroidDev")) return "توسعه‌دهنده";
    return "کاربر عادی";
  };

  const getUserTypeColor = () => {
    if (!userInfo?.type_user) return "#95a5a6";
    const types = userInfo.type_user;
    if (types.includes("userPremium")) return "#f39c12";
    if (types.includes("subtitlesManager")) return "#3498db";
    if (types.includes("appAndroidDev")) return "#9b59b6";
    return "#95a5a6";
  };

  const handleRequestDelete = () => {
    setStep("confirm");
    setConfirmText("");
    setError("");
  };

  const handleSubmitDelete = async () => {
    if (confirmText !== "حذف") {
      setError("لطفاً عبارت «حذف» را به درستی وارد کنید");
      return;
    }

    setSubmitting(true);
    setError("");

    try {
      const response = await deleteAccountApi();
      if (response?.data?.success) {
        setStep("success");
      } else {
        setError(
          response?.data?.message || "خطا در ثبت درخواست حذف حساب"
        );
      }
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "خطا در ارتباط با سرور";
      if (message.includes("قبلاً")) {
        setAlreadyRequested(true);
        setStep("success");
      } else {
        setError(message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="delete-account-page">
        <header className="delete-account-header">
          <button className="back-btn" onClick={() => navigate("/")}>
            ← بازگشت
          </button>
          <h1>حذف حساب کاربری</h1>
        </header>
        <div className="delete-account-content">
          <div className="loading-section">
            <div className="spinner"></div>
            <p>در حال بارگذاری اطلاعات...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="delete-account-page">
      <header className="delete-account-header">
        <button className="back-btn" onClick={() => navigate("/")}>
          ← بازگشت
        </button>
        <h1>حذف حساب کاربری</h1>
      </header>

      <div className="delete-account-content">
        <div className="delete-account-card">
          {error && step !== "success" && (
            <div className="error-message">
              <i className="fas fa-exclamation-circle"></i> {error}
            </div>
          )}

          {userInfo && (
            <div className="user-info-section">
              <div className="user-avatar-icon">
                <i className="fas fa-user-circle"></i>
              </div>
              <div className="user-info-details">
                <h3>{userInfo.userName || "کاربر"}</h3>
                <span
                  className="user-type-badge"
                  style={{ backgroundColor: getUserTypeColor() }}
                >
                  {getUserTypeDisplay()}
                </span>
              </div>
            </div>
          )}

          {step === "initial" && (
            <>
              <p className="initial-text">
                در این بخش می‌توانید درخواست حذف حساب کاربری خود را ثبت کنید.
                پس از ثبت درخواست، حساب شما به مدت ۷ روز غیرفعال می‌شود و سپس
                به طور کامل حذف خواهد شد.
              </p>

              <div className="warning-section">
                <div className="warning-icon">
                  <i className="fas fa-exclamation-triangle"></i>
                </div>
                <h2>هشدار!</h2>
                <p>
                  با حذف حساب کاربری، تمام اطلاعات زیر برای همیشه از بین
                  خواهد رفت:
                </p>
                <ul className="warning-list">
                  <li>
                    <i className="fas fa-times"></i>
                    تمام زیرنویس‌های ذخیره شده
                  </li>
                  <li>
                    <i className="fas fa-times"></i>
                    سابقه فعالیت و پیشرفت شما
                  </li>
                  <li>
                    <i className="fas fa-times"></i>
                    اطلاعات اشتراک و پکیج‌های خریداری شده
                  </li>
                  <li>
                    <i className="fas fa-times"></i>
                    لیست کلمات و لغت‌نامه شخصی شما
                  </li>
                </ul>
              </div>

              <button className="delete-btn" onClick={handleRequestDelete}>
                <i className="fas fa-trash-alt"></i>
                درخواست حذف حساب
              </button>
            </>
          )}

          {step === "confirm" && (
            <>
              <div className="warning-section">
                <div className="warning-icon">
                  <i className="fas fa-exclamation-triangle"></i>
                </div>
                <h2>آیا مطمئن هستید؟</h2>
                <p>
                  این اقدام غیرقابل بازگشت است. پس از ۷ روز حساب شما به طور
                  کامل حذف خواهد شد.
                </p>
              </div>

              <div className="info-section">
                <h3>انصراف از حذف</h3>
                <p>
                  اگر تا ۷ روز آینده وارد حساب خود شوید، درخواست حذف
                  خودبه‌خود لغو می‌شود.
                </p>
              </div>

              <div className="confirm-input-section">
                <p>
                  برای تأیید، عبارت{" "}
                  <strong style={{ color: "var(--accent-danger, #ff6b6b)" }}>
                    حذف
                  </strong>{" "}
                  را وارد کنید:
                </p>
                <input
                  type="text"
                  className="confirm-input"
                  value={confirmText}
                  onChange={(e) => {
                    setConfirmText(e.target.value);
                    setError("");
                  }}
                  placeholder="عبارت «حذف» را وارد کنید"
                  disabled={submitting}
                />
              </div>

              <button
                className="delete-btn"
                onClick={handleSubmitDelete}
                disabled={submitting || confirmText !== "حذف"}
                style={{ marginTop: "16px" }}
              >
                {submitting ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    در حال پردازش...
                  </>
                ) : (
                  <>
                    <i className="fas fa-trash-alt"></i>
                    تأیید و حذف حساب
                  </>
                )}
              </button>
            </>
          )}

          {step === "success" && (
            <div className="success-section">
              <div className="success-icon">
                <i className="fas fa-check-circle"></i>
              </div>
              <h2>
                {alreadyRequested
                  ? "درخواست حذف قبلاً ثبت شده است"
                  : "درخواست حذف با موفقیت ثبت شد"}
              </h2>
              <p>
                حساب شما از این لحظه غیرفعال شد و پس از ۷ روز به طور کامل
                حذف خواهد شد.
              </p>
              <p>
                اگر تا ۷ روز آینده وارد حساب خود شوید، درخواست حذف
                خودبه‌خود لغو می‌شود.
              </p>
              <button
                className="back-btn"
                onClick={() => navigate("/")}
                style={{ marginTop: "24px" }}
              >
                ← بازگشت به صفحه اصلی
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DeleteAccountPage;
